import type { IAPIAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/abstract/IAPIAdapter';
import { AbstractDataServiceFeature } from 'plugins/tag/plugin_data_service/ts/Application/abstract/AbstractDataServiceFeature';
import { IUserListEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUserListEndpoint';
import { IDataServiceFeatureContext } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeatureContext';
import { IUserListMutationParams } from 'tag/ts/tag/interfaces/api/IUserListMutationParams';

const USER_LISTS_FEATURE_LOG_TAG = '[UserListsFeature]';

/** Active adapter implementing {@link IUserListEndpoint}, or null when missing or not capable. */
function userListAdapter( adapter: IAPIAdapter | null ): IUserListEndpoint | null {
	if ( adapter == null ) {
		return null;
	}
	const asEndpoint = adapter as unknown as IUserListEndpoint;
	if (
		typeof asEndpoint.addToMyList !== 'function'
		|| typeof asEndpoint.removeFromMyList !== 'function'
		|| typeof asEndpoint.addToFavorites !== 'function'
		|| typeof asEndpoint.removeFromFavorites !== 'function'
	) {
		return null;
	}
	return asEndpoint;
}

/**
 * @copyright © Paramount Streaming
 * @description Data Service feature exposing My List / Favorites add-remove mutations via
 * `tag.API.DATA.UserLists`. Delegates to the active adapter's {@link IUserListEndpoint}
 * (converged Pluto implements HTTP to apps-api user watchlist/favorites endpoints).
 */
export class UserListsFeature extends AbstractDataServiceFeature {

	#userListEndpoint: IUserListEndpoint | null = null;

	get name (): string {
		return 'UserListsFeature';
	}

	get featureKey (): string {
		return 'UserLists';
	}

	getPublicAPI (): Record<string, unknown> {
		return {
			addToMyList: this.#addToMyList,
			removeFromMyList: this.#removeFromMyList,
			addToFavorites: this.#addToFavorites,
			removeFromFavorites: this.#removeFromFavorites
		};
	}

	protected doInstall( context: IDataServiceFeatureContext ): void {
		const activeAdapter: IAPIAdapter | null = this.getApiService().getActiveAdapter();
		this.#userListEndpoint = userListAdapter( activeAdapter );
		if ( !this.#userListEndpoint ) {
			this.logger?.warn( `${ USER_LISTS_FEATURE_LOG_TAG } active adapter does not support IUserListEndpoint; UserLists API will throw if invoked` );
		}
	}

	#addToMyList = async ( params: IUserListMutationParams ): Promise<unknown> => {
		const ep = this.#requireEndpoint();
		this.logger?.log( USER_LISTS_FEATURE_LOG_TAG, 'addToMyList', params?.contentId );
		return ep.addToMyList( params, this.context?.tag?.params );
	};

	#removeFromMyList = async ( params: IUserListMutationParams ): Promise<unknown> => {
		const ep = this.#requireEndpoint();
		this.logger?.log( USER_LISTS_FEATURE_LOG_TAG, 'removeFromMyList', params?.contentId );
		return ep.removeFromMyList( params, this.context?.tag?.params );
	};

	#addToFavorites = async ( params: IUserListMutationParams ): Promise<unknown> => {
		const ep = this.#requireEndpoint();
		this.logger?.log( USER_LISTS_FEATURE_LOG_TAG, 'addToFavorites', params?.contentId );
		return ep.addToFavorites( params, this.context?.tag?.params );
	};

	#removeFromFavorites = async ( params: IUserListMutationParams ): Promise<unknown> => {
		const ep = this.#requireEndpoint();
		this.logger?.log( USER_LISTS_FEATURE_LOG_TAG, 'removeFromFavorites', params?.contentId );
		return ep.removeFromFavorites( params, this.context?.tag?.params );
	};

	#requireEndpoint = (): IUserListEndpoint => {
		const ep = this.#userListEndpoint;
		if ( !ep ) {
			throw new Error( `${ USER_LISTS_FEATURE_LOG_TAG } user list endpoint not available on the active adapter` );
		}
		return ep;
	};
}